import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { GraphNode, GraphLink } from './types';
import { SYMBOLS, getCommsTier } from './utils/graphUtils';
import { Focus } from 'lucide-react';

interface D3HybridViewerProps {
    nodes: GraphNode[];
    links: GraphLink[];
    onNodeClick?: (node: GraphNode) => void;
    orientation: 'TD' | 'LR';
    expandedIds: Set<string>;
    onToggleExpand: (nodeId: string) => void;
    powerVisMode: 'none' | 'shadow' | 'halo' | 'bar';
    selectedNodeIds: Set<string>;
    onBackgroundClick?: () => void;
    visibleNodeIds: Set<string>;
    highlightedPath: Set<string>;
    sidebarWidth?: number;
    nodeClickPhase?: number; // 0=none, 1=selected, 2=one-level, 3=full chain
    expandingNodeId?: string | null; // Node currently loading children
}

// Extended node type for simulation
interface SimNode extends GraphNode {
    x: number;
    y: number;
    vx: number;
    vy: number;
    fx: number | null;
    fy: number | null;
    targetCross: number;
    effectiveTier: number;
    anchorX?: number; // Calculated fixed X for ancillary nodes (restore after drag)
    anchorY?: number; // Calculated fixed Y for ancillary nodes (restore after drag)
}

// Extended link type for simulation
interface SimLink {
    source: SimNode | string;
    target: SimNode | string;
    color: string;
    details: { signal?: string; relation?: string };
}

const D3HybridViewer: React.FC<D3HybridViewerProps> = ({
    nodes,
    links,
    onNodeClick,
    orientation,
    expandedIds,
    onToggleExpand,
    powerVisMode,
    selectedNodeIds,
    onBackgroundClick,
    visibleNodeIds,
    highlightedPath,
    sidebarWidth = 0,
    nodeClickPhase = 0,
    expandingNodeId,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const [cursorStyle, setCursorStyle] = useState<'default' | 'pointer' | 'grab' | 'grabbing'>('default');
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

    const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
    const nodeStateRef = useRef<Map<string, { x: number, y: number, vx: number, vy: number }>>(new Map());
    const simulationRef = useRef<d3.Simulation<SimNode, SimLink> | null>(null);
    const d3NodesRef = useRef<SimNode[]>([]);
    const d3LinksRef = useRef<SimLink[]>([]);
    const nodeMapRef = useRef<Map<string, SimNode>>(new Map());
    const dragNodeRef = useRef<SimNode | null>(null);
    const isDraggingRef = useRef(false);
    const animationFrameRef = useRef<number | null>(null);
    const drawFnRef = useRef<(() => void) | null>(null);
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<HTMLCanvasElement, unknown> | null>(null);
    const needsRedrawRef = useRef(false);
    const prevNodeCountRef = useRef(0);

    // Precomputed per-node flags (computed once when topology changes, not per frame)
    const nodeFlagsRef = useRef<Map<string, {
        childCount: number;
        hasHiddenChildren: boolean;
        hasUnrenderedCommLinks: boolean;
    }>>(new Map());

    // Cached Path2D shapes per symbol type
    const symbolCacheRef = useRef<Map<string, Path2D>>(new Map());

    // Resize observer
    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    // Center view on specified nodes (or all visible nodes if none specified)
    // Maximum zoom out is 0.5 (where labels start to disappear)
    const centerOnNodes = useCallback((nodeIds?: string[]) => {
        const canvas = canvasRef.current;
        const zoomBehavior = zoomBehaviorRef.current;
        if (!canvas || !zoomBehavior) return;

        const simNodes = d3NodesRef.current;
        if (simNodes.length === 0) return;

        // Get nodes to center on
        let targetNodes: SimNode[];
        if (nodeIds && nodeIds.length > 0) {
            const nodeIdSet = new Set(nodeIds);
            targetNodes = simNodes.filter(n => nodeIdSet.has(n.id));
        } else {
            // Default to all visible nodes
            targetNodes = simNodes.filter(n => visibleNodeIds.has(n.id));
        }

        if (targetNodes.length === 0) {
            targetNodes = simNodes; // Fallback to all nodes
        }

        // Calculate bounding box
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        targetNodes.forEach(node => {
            if (node.x < minX) minX = node.x;
            if (node.x > maxX) maxX = node.x;
            if (node.y < minY) minY = node.y;
            if (node.y > maxY) maxY = node.y;
        });

        // Add padding
        const padding = 50;
        minX -= padding;
        maxX += padding;
        minY -= padding;
        maxY += padding;

        const boxWidth = maxX - minX;
        const boxHeight = maxY - minY;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // Calculate available viewport (accounting for sidebar)
        const viewportWidth = dimensions.width - sidebarWidth;
        const viewportHeight = dimensions.height;

        // Calculate zoom to fit, with max zoom out of 0.5 (labels disappear below this)
        const scaleX = viewportWidth / boxWidth;
        const scaleY = viewportHeight / boxHeight;
        let targetScale = Math.min(scaleX, scaleY, 2); // Don't zoom in more than 2x
        targetScale = Math.max(targetScale, 0.5); // Don't zoom out more than 0.5 (label threshold)

        // Calculate translation to center the nodes in the visible viewport
        // The visible viewport center is shifted left by half the sidebar width
        const viewportCenterX = (viewportWidth / 2);
        const viewportCenterY = viewportHeight / 2;

        const targetX = viewportCenterX - centerX * targetScale;
        const targetY = viewportCenterY - centerY * targetScale;

        // Animate to new transform
        const canvasSelection = d3.select(canvas);
        const targetTransform = d3.zoomIdentity.translate(targetX, targetY).scale(targetScale);

        canvasSelection
            .transition()
            .duration(500)
            .call(zoomBehavior.transform, targetTransform);
    }, [dimensions, sidebarWidth, visibleNodeIds]);

    // Pan to center on specified nodes without changing zoom level
    const panToNodes = useCallback((nodeIds: string[]) => {
        const canvas = canvasRef.current;
        const zoomBehavior = zoomBehaviorRef.current;
        if (!canvas || !zoomBehavior || nodeIds.length === 0) return;

        const simNodes = d3NodesRef.current;
        if (simNodes.length === 0) return;

        // Get nodes to center on
        const nodeIdSet = new Set(nodeIds);
        const targetNodes = simNodes.filter(n => nodeIdSet.has(n.id));
        if (targetNodes.length === 0) return;

        // Calculate center of selection
        let sumX = 0, sumY = 0;
        targetNodes.forEach(node => {
            sumX += node.x;
            sumY += node.y;
        });
        const centerX = sumX / targetNodes.length;
        const centerY = sumY / targetNodes.length;

        // Keep current zoom level
        const currentScale = transformRef.current.k;

        // Calculate available viewport (accounting for sidebar)
        const viewportWidth = dimensions.width - sidebarWidth;
        const viewportHeight = dimensions.height;
        const viewportCenterX = viewportWidth / 2;
        const viewportCenterY = viewportHeight / 2;

        // Calculate translation to center selection
        const targetX = viewportCenterX - centerX * currentScale;
        const targetY = viewportCenterY - centerY * currentScale;

        // Animate pan only
        const canvasSelection = d3.select(canvas);
        const targetTransform = d3.zoomIdentity.translate(targetX, targetY).scale(currentScale);

        canvasSelection
            .transition()
            .duration(300)
            .call(zoomBehavior.transform, targetTransform);
    }, [dimensions, sidebarWidth]);

    // Auto-pan to selected nodes when selection changes
    useEffect(() => {
        if (selectedNodeIds.size > 0) {
            // Small delay to ensure nodes have positions after any simulation updates
            const timer = setTimeout(() => {
                panToNodes(Array.from(selectedNodeIds));
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [selectedNodeIds, panToNodes]);

    // Helper: Plasma Color Scale for power visualization
    const getPlasmaColor = useCallback((t: number): string => {
        if (t < 0.2) return d3.interpolateRgb("#0d0887", "#5402a3")(t / 0.2);
        if (t < 0.4) return d3.interpolateRgb("#5402a3", "#900da3")(t / 0.2 - 1);
        if (t < 0.6) return d3.interpolateRgb("#900da3", "#cb4778")(t / 0.2 - 2);
        if (t < 0.8) return d3.interpolateRgb("#cb4778", "#f0894f")(t / 0.2 - 3);
        return d3.interpolateRgb("#f0894f", "#fcce25")(t / 0.2 - 4);
    }, []);

    // Helper: Get cached Path2D for a symbol type (parsed once, reused every frame)
    const getSymbolPath2D = useCallback((type: string): Path2D => {
        const cache = symbolCacheRef.current;
        let cached = cache.get(type);
        if (cached) return cached;

        // Parse SVG path string and build Path2D once
        const pathStr = (() => {
            switch (type) {
                // Grid & Utility
                case 'grid_connection_point': return SYMBOLS.grid;
                case 'grid': return SYMBOLS.grid;

                // Transformers
                case 'transformer': return SYMBOLS.transformer;
                case 'control_transformer': return SYMBOLS.control_transformer;
                case 'current_transformer': return SYMBOLS.current_transformer;
                case 'voltage_transformer': return SYMBOLS.voltage_transformer;

                // Power Conversion
                case 'inverter': return SYMBOLS.inverter;
                case 'pcs_bidirectional': return SYMBOLS.pcs_bidirectional;
                case 'mppt': return SYMBOLS.mppt;
                case 'dc_optimizer': return SYMBOLS.dc_optimizer;
                case 'microinverter': return SYMBOLS.microinverter;

                // Protection Devices
                case 'circuit_breaker': return SYMBOLS.breaker;
                case 'fuse': return SYMBOLS.fuse;
                case 'disconnect_switch': return SYMBOLS.switch;
                case 'protective_relay': return SYMBOLS.protective_relay;
                case 'surge_protection_device': return SYMBOLS.surge_protection_device;
                case 'rapid_shutdown_device': return SYMBOLS.rapid_shutdown_device;

                // Distribution Equipment
                case 'pv_module': return SYMBOLS.module;
                case 'string': return SYMBOLS.string;
                case 'combiner_box': return SYMBOLS.combiner_box;
                case 'junction_box': return SYMBOLS.junction_box;
                case 'panelboard': return SYMBOLS.panelboard;
                case 'switchboard_lv': return SYMBOLS.switchboard_lv;
                case 'switchgear_mv': return SYMBOLS.switchgear_mv;

                // Metering
                case 'revenue_meter': return SYMBOLS.revenue_meter;

                // Monitoring & Weather
                case 'gateway': return SYMBOLS.gateway;
                case 'weather_station': return SYMBOLS.weather_station;
                case 'pyranometer': return SYMBOLS.pyranometer;
                case 'ambient_temp_sensor': return SYMBOLS.ambient_temp_sensor;
                case 'module_temp_sensor': return SYMBOLS.module_temp_sensor;
                case 'anemometer': return SYMBOLS.anemometer;
                case 'rain_gauge': return SYMBOLS.rain_gauge;

                // Infrastructure
                case 'grounding_system': return SYMBOLS.grounding_system;
                case 'racking_system': return SYMBOLS.racking_system;
                case 'tracker_controller': return SYMBOLS.tracker_controller;

                default: return SYMBOLS.default;
            }
        })();

        const path = new Path2D();
        const commands = pathStr.match(/[MLQTCSAZ][^MLQTCSAZ]*/gi) || [];

        commands.forEach(cmd => {
            const t = cmd[0].toUpperCase();
            const args = cmd.slice(1).trim().split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));

            switch (t) {
                case 'M': path.moveTo(args[0], args[1]); break;
                case 'L': path.lineTo(args[0], args[1]); break;
                case 'Q': path.quadraticCurveTo(args[0], args[1], args[2], args[3]); break;
                case 'T': path.lineTo(args[0], args[1]); break;
                case 'C': path.bezierCurveTo(args[0], args[1], args[2], args[3], args[4], args[5]); break;
                case 'A':
                    if (args.length >= 7) {
                        path.arc(args[5], args[6], args[0], 0, Math.PI * 2);
                    }
                    break;
                case 'Z': path.closePath(); break;
            }
        });

        cache.set(type, path);
        return path;
    }, []);

    // Draw a single node
    const drawNode = useCallback((
        ctx: CanvasRenderingContext2D,
        node: SimNode,
        isSelected: boolean,
        isInPath: boolean,
        isHighlighting: boolean,
        childCount: number,
        isExpanded: boolean,
        hasHiddenChildren: boolean,
        hasUnrenderedCommLinks: boolean,
        labelColor: string
    ) => {
        ctx.save();
        ctx.translate(node.x, node.y);

        // Calculate opacity based on selection/highlighting state
        // Non-highlighted/non-selected items are heavily faded for visual focus
        let opacity = 1;
        if (isHighlighting) {
            opacity = isInPath ? 1 : 0.5;  // 90% transparent for non-path items
        } else if (selectedNodeIds.size > 0) {
            opacity = isSelected ? 1 : 0.55;  // 85% transparent for non-selected items
        }

        // Power visualization - halo
        if (powerVisMode === 'halo' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const color = getPlasmaColor(node.normalizedPower);
            const radius = 12 + (node.normalizedPower * 8);
            ctx.beginPath();
            ctx.arc(0, 0, radius, 0, Math.PI * 2);
            ctx.strokeStyle = color;
            ctx.lineWidth = 2 + (node.normalizedPower * 3);
            ctx.globalAlpha = 0.8 * opacity;
            ctx.stroke();
        }

        // Power visualization - bar
        if (powerVisMode === 'bar' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const color = getPlasmaColor(node.normalizedPower);
            const barHeight = 10 + (node.normalizedPower * 20);
            ctx.fillStyle = color;
            ctx.globalAlpha = opacity;
            ctx.beginPath();
            ctx.roundRect(14, -barHeight / 2, 4, barHeight, 2);
            ctx.fill();
        }

        // Draw node shape (uses cached Path2D)
        const path = getSymbolPath2D(node.type);

        // Fill with darker color
        const baseColor = d3.color(node.color);
        ctx.fillStyle = baseColor?.darker(0.5).toString() || "#000";
        ctx.globalAlpha = opacity;
        ctx.fill(path);

        // Stroke
        ctx.strokeStyle = isSelected ? "#10b981" : node.color;
        ctx.lineWidth = isSelected ? 3 : 2;

        // Power shadow effect
        if (powerVisMode === 'shadow' && node.normalizedPower !== undefined && node.cumulativePower && node.cumulativePower > 0) {
            const shadowColor = getPlasmaColor(node.normalizedPower);
            const blur = 3 + (node.normalizedPower * 5);
            ctx.shadowColor = shadowColor;
            ctx.shadowBlur = blur;
        }

        ctx.stroke(path);
        ctx.shadowBlur = 0;

        // Draw label
        if (node.type !== 'pv_module') {
            ctx.fillStyle = labelColor;
            ctx.globalAlpha = opacity;
            ctx.font = "10px monospace";
            ctx.textAlign = "center";
            ctx.textBaseline = "top";

            // Check for aggregated nodes - show first item name + additional count
            const aggCount = node.details?.properties?.["Aggregated Count"] as number | undefined;
            const aggIds = node.details?.properties?.["Aggregated IDs"] as string | undefined;
            let label = node.id;
            if (aggCount && aggIds) {
                const firstName = aggIds.split(',')[0].trim();
                label = `${firstName} (+${aggCount - 1})`;
            }
            ctx.fillText(label, 0, 22);
        }

        // Expand/Collapse toggle
        const isLoadingNode = expandingNodeIdRef.current === node.id;
        if (childCount > 0 || isLoadingNode) {
            ctx.save();
            ctx.translate(15, -15);

            if (isLoadingNode) {
                // Loading spinner: rotating arc
                const angle = loadingAngleRef.current;
                ctx.beginPath();
                ctx.arc(0, 0, 10, 0, Math.PI * 2);
                ctx.fillStyle = "#1f2937";
                ctx.fill();
                ctx.strokeStyle = "#10b981";
                ctx.lineWidth = 2.5;
                ctx.beginPath();
                ctx.arc(0, 0, 10, angle, angle + Math.PI * 1.2);
                ctx.stroke();
            } else {
                // Toggle circle background
                ctx.beginPath();
                ctx.arc(0, 0, 10, 0, Math.PI * 2);
                ctx.fillStyle = "#1f2937";
                ctx.fill();
                ctx.strokeStyle = isExpanded ? "#ef4444" : "#10b981";
                ctx.lineWidth = 2;
                ctx.stroke();

                // Toggle text: "-" when expanded, child count when collapsed
                ctx.fillStyle = "#fff";
                ctx.font = "bold 10px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(isExpanded ? "-" : String(childCount), 0, 0);
            }

            ctx.restore();
        }

        // Stub for hidden hierarchical children
        if (hasHiddenChildren) {
            ctx.strokeStyle = "#9CA3AF";
            ctx.lineWidth = 2;
            ctx.setLineDash([2, 2]);
            ctx.beginPath();
            if (orientation === 'TD') {
                ctx.moveTo(0, 20);
                ctx.lineTo(0, 35);
            } else {
                ctx.moveTo(20, 0);
                ctx.lineTo(35, 0);
            }
            ctx.stroke();
            ctx.setLineDash([]);

            // Stub dot
            ctx.beginPath();
            ctx.arc(orientation === 'TD' ? 0 : 35, orientation === 'TD' ? 35 : 0, 2, 0, Math.PI * 2);
            ctx.fillStyle = "#9CA3AF";
            ctx.fill();
        }

        // Stub for unrendered comm links
        if (hasUnrenderedCommLinks) {
            ctx.strokeStyle = "#3B82F6";
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 2]);
            ctx.beginPath();
            if (orientation === 'TD') {
                ctx.moveTo(-20, 0);
                ctx.lineTo(-35, 0);
            } else {
                ctx.moveTo(0, -20);
                ctx.lineTo(0, -35);
            }
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.beginPath();
            ctx.arc(orientation === 'TD' ? -35 : 0, orientation === 'TD' ? 0 : -35, 2, 0, Math.PI * 2);
            ctx.fillStyle = "#3B82F6";
            ctx.fill();
        }

        ctx.restore();
    }, [orientation, powerVisMode, selectedNodeIds, getPlasmaColor, getSymbolPath2D]);

    // Draw a node at medium detail (symbol only, no labels/buttons)
    const drawNodeMediumDetail = useCallback((
        ctx: CanvasRenderingContext2D,
        node: SimNode,
        isSelected: boolean,
        isInPath: boolean,
        isHighlighting: boolean
    ) => {
        ctx.save();
        ctx.translate(node.x, node.y);

        // Calculate opacity
        let opacity = 1;
        if (isHighlighting) {
            opacity = isInPath ? 1 : 0.3;
        } else if (selectedNodeIds.size > 0) {
            opacity = isSelected ? 1 : 0.4;
        }

        // Draw node shape (uses cached Path2D)
        const path = getSymbolPath2D(node.type);

        // Fill with darker color
        const baseColor = d3.color(node.color);
        ctx.fillStyle = baseColor?.darker(0.5).toString() || "#000";
        ctx.globalAlpha = opacity;
        ctx.fill(path);

        // Stroke
        ctx.strokeStyle = isSelected ? "#10b981" : node.color;
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.stroke(path);

        ctx.restore();
    }, [selectedNodeIds, getSymbolPath2D]);

    // Draw a single link
    const drawLink = useCallback((ctx: CanvasRenderingContext2D, link: SimLink, nodeMap: Map<string, SimNode>) => {
        let s = typeof link.source === 'object' ? link.source : nodeMap.get(link.source as string);
        let t = typeof link.target === 'object' ? link.target : nodeMap.get(link.target as string);

        if (!s || !t) return;

        const x1 = s.x, y1 = s.y, x2 = t.x, y2 = t.y;
        const mainDiff = orientation === 'TD' ? Math.abs(y1 - y2) : Math.abs(x1 - x2);
        const isComm = link.details?.signal === 'COMM';

        ctx.beginPath();
        ctx.strokeStyle = link.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.6;

        if (isComm) {
            ctx.setLineDash([4, 2]);
        }

        if (mainDiff < 30) {
            // Arc for same-tier connections
            const dr = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) * 0.5;
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const perpX = -(y2 - y1) / Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + 0.001);
            const perpY = (x2 - x1) / Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + 0.001);
            const ctrlX = midX + perpX * dr;
            const ctrlY = midY + perpY * dr;

            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(ctrlX, ctrlY, x2, y2);
        } else {
            // Cubic bezier curve for hierarchical connections
            if (orientation === 'TD') {
                const midY = (y1 + y2) / 2;
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(x1, midY, x2, midY, x2, y2);
            } else {
                const midX = (x1 + x2) / 2;
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(midX, y1, midX, y2, x2, y2);
            }
        }

        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
    }, [orientation]);

    // Draw containment hulls (convex boundaries around contained nodes)
    // pcs_skid nodes are not rendered, but we draw hulls around their contained components
    const drawContainmentHulls = useCallback((
        ctx: CanvasRenderingContext2D,
        d3Links: SimLink[],
        nodeMap: Map<string, SimNode>,
        visibleNodeIdSet: Set<string>
    ) => {
        // Build containment groups: container -> Set of contained node IDs
        const containmentGroups = new Map<string, Set<string>>();

        d3Links.forEach(link => {
            if (link.details?.relation === 'contains') {
                // Source contains Target
                const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                const targetId = typeof link.target === 'object' ? link.target.id : link.target;

                if (!containmentGroups.has(sourceId)) {
                    containmentGroups.set(sourceId, new Set());
                }
                containmentGroups.get(sourceId)!.add(targetId);
            }
        });

        // For each container, compute and draw convex hull around contained nodes only
        // (container node like pcs_skid is not rendered)
        containmentGroups.forEach((containedIds, containerId) => {
            // Get container node info for color and label
            const containerNode = nodeMap.get(containerId);
            const containerType = containerNode?.type || 'unknown';
            const containerLabel = containerId;

            // Collect positions of contained nodes only
            const points: [number, number][] = [];
            const padding = 30; // Padding around nodes

            // Add contained nodes positions
            containedIds.forEach(nodeId => {
                const node = nodeMap.get(nodeId);
                if (node && visibleNodeIdSet.has(nodeId)) {
                    points.push([node.x - padding, node.y - padding]);
                    points.push([node.x - padding, node.y + padding]);
                    points.push([node.x + padding, node.y - padding]);
                    points.push([node.x + padding, node.y + padding]);
                }
            });

            // Need at least 3 points for a hull (at least 1 visible contained node)
            if (points.length < 3) return;

            // Compute convex hull using d3
            const hull = d3.polygonHull(points);
            if (!hull || hull.length < 3) return;

            // Draw the hull
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(hull[0][0], hull[0][1]);

            for (let i = 1; i < hull.length; i++) {
                ctx.lineTo(hull[i][0], hull[i][1]);
            }
            ctx.closePath();

            // Determine hull color based on container type
            // Gateway and weather_station containers get light blue, others get pcs_skid amber color
            const hullColor = (containerType === 'gateway' || containerType === 'weather_station') ? '#7DD3FC' : '#B45309';

            ctx.fillStyle = hullColor;
            ctx.globalAlpha = 0.1;
            ctx.fill();

            // Stroke with dashed border
            ctx.strokeStyle = hullColor;
            ctx.globalAlpha = 0.5;
            ctx.lineWidth = 2;
            ctx.setLineDash([8, 4]);
            ctx.stroke();
            ctx.setLineDash([]);

            // Draw label at top of hull
            let minY = Infinity;
            let labelX = 0;
            hull.forEach(([x, y]) => {
                if (y < minY) {
                    minY = y;
                    labelX = x;
                }
            });

            ctx.globalAlpha = 0.8;
            ctx.fillStyle = hullColor;
            ctx.font = "bold 11px monospace";
            ctx.textAlign = "left";
            ctx.textBaseline = "bottom";
            ctx.fillText(containerLabel, labelX + 5, minY - 5);

            ctx.restore();
        });
    }, []);

    // Main draw function with viewport culling and LOD
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Get theme-aware label color from CSS variables
        const labelColor = getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim() || '#e5e7eb';

        const dpr = window.devicePixelRatio || 1;
        const width = dimensions.width;
        const height = dimensions.height;

        // Clear canvas
        ctx.save();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        // Apply zoom transform
        const t = transformRef.current;
        ctx.translate(t.x, t.y);
        ctx.scale(t.k, t.k);

        const nodeMap = nodeMapRef.current;
        const d3Nodes = d3NodesRef.current;
        const d3Links = d3LinksRef.current;

        // === VIEWPORT CULLING ===
        // Calculate visible bounds in world coordinates (with margin for nodes near edges)
        const margin = 50; // Extra margin to catch nodes partially visible
        const viewBounds = {
            x1: (-t.x / t.k) - margin,
            y1: (-t.y / t.k) - margin,
            x2: ((width - t.x) / t.k) + margin,
            y2: ((height - t.y) / t.k) + margin
        };

        // Filter to visible nodes only (exclude pcs_skid - rendered as hull only)
        const visibleD3Nodes = d3Nodes.filter(node =>
            node.type !== 'pcs_skid' &&
            node.x >= viewBounds.x1 &&
            node.x <= viewBounds.x2 &&
            node.y >= viewBounds.y1 &&
            node.y <= viewBounds.y2
        );

        // Create set of visible node IDs for link filtering
        const visibleNodeIdSet = new Set(visibleD3Nodes.map(n => n.id));

        // Filter links where at least one endpoint is visible
        // Exclude links involving pcs_skid (they're not rendered, only used for hull)
        const visibleD3Links = d3Links.filter(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            const sourceNode = nodeMap.get(sourceId);
            const targetNode = nodeMap.get(targetId);
            // Skip links to/from pcs_skid
            if (sourceNode?.type === 'pcs_skid' || targetNode?.type === 'pcs_skid') return false;
            return visibleNodeIdSet.has(sourceId) || visibleNodeIdSet.has(targetId);
        });

        // === LOD (Level of Detail) ===
        // Force low detail mode if too many nodes visible (performance threshold)
        const zoomLevel = t.k;
        const tooManyNodes = visibleD3Nodes.length > 3000;
        const isLowDetail = zoomLevel < 0.25 || tooManyNodes;
        const isMediumDetail = !tooManyNodes && zoomLevel >= 0.25 && zoomLevel < 0.5;

        // Draw containment hulls first (behind everything)
        if (!isLowDetail) {
            drawContainmentHulls(ctx, d3Links, nodeMap, visibleNodeIdSet);
        }

        // Draw links first (below nodes)
        // At very low zoom, skip link rendering entirely for performance
        if (!isLowDetail) {
            visibleD3Links.forEach(link => {
                // At medium detail, skip links to/from modules
                if (isMediumDetail) {
                    const sourceNode = typeof link.source === 'object' ? link.source : nodeMap.get(link.source as string);
                    const targetNode = typeof link.target === 'object' ? link.target : nodeMap.get(link.target as string);
                    if (sourceNode?.type === 'pv_module' || targetNode?.type === 'pv_module') return;
                }
                drawLink(ctx, link, nodeMap);
            });
        }

        // Draw nodes (use precomputed flags for performance)
        const isHighlighting = highlightedPath.size > 0;
        const flags = nodeFlagsRef.current;

        visibleD3Nodes.forEach(node => {
            const isSelected = selectedNodeIds.has(node.id);
            const isInPath = highlightedPath.has(node.id);
            const isExpanded = expandedIds.has(node.id);

            // Use precomputed flags (O(1) lookup instead of O(links) per node)
            const nodeFlags = flags.get(node.id);
            const childCount = nodeFlags?.childCount ?? 0;
            const hasHiddenChildren = nodeFlags?.hasHiddenChildren ?? false;
            const hasUnrenderedCommLinks = nodeFlags?.hasUnrenderedCommLinks ?? false;

            // === LOD RENDERING ===
            if (isLowDetail) {
                // Skip modules at low detail - they're too numerous
                if (node.type === 'pv_module') return;

                // Very low zoom or many nodes: simple colored dots
                ctx.save();
                ctx.beginPath();
                ctx.arc(node.x, node.y, 12, 0, Math.PI * 2);
                ctx.fillStyle = node.color;
                ctx.globalAlpha = isHighlighting ? (isInPath ? 1 : 0.3) : (selectedNodeIds.size > 0 ? (isSelected ? 1 : 0.4) : 1);
                ctx.fill();
                if (isSelected) {
                    ctx.strokeStyle = "#10b981";
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }
                ctx.restore();
            } else if (isMediumDetail) {
                // Skip modules at medium detail - they're too numerous
                if (node.type === 'pv_module') return;

                // Medium zoom: symbols but no labels, no expand buttons
                drawNodeMediumDetail(ctx, node, isSelected, isInPath, isHighlighting);
            } else {
                // Full detail
                drawNode(ctx, node, isSelected, isInPath, isHighlighting, childCount, isExpanded, hasHiddenChildren, hasUnrenderedCommLinks, labelColor);
            }
        });

        ctx.restore();
    }, [dimensions, drawLink, drawNode, drawNodeMediumDetail, drawContainmentHulls, highlightedPath, selectedNodeIds, expandedIds]);

    // Keep draw function ref updated
    useEffect(() => {
        drawFnRef.current = draw;
    }, [draw]);

    // Precompute node flags for VISIBLE nodes only (not all 38k nodes!)
    useEffect(() => {
        // const t0 = performance.now();
        const flags = new Map<string, {
            childCount: number;
            hasHiddenChildren: boolean;
            hasUnrenderedCommLinks: boolean;
        }>();

        // Build adjacency maps once - only for links touching visible nodes
        // Use Sets to avoid counting duplicate children from multiple links
        const childrenMap = new Map<string, Set<string>>();
        const commAdjMap = new Map<string, string[]>();

        for (const link of links) {
            const source = String(link.source);
            const target = String(link.target);

            // Only process links where at least one endpoint is visible
            const sourceVisible = visibleNodeIds.has(source);
            const targetVisible = visibleNodeIds.has(target);
            if (!sourceVisible && !targetVisible) continue;

            if (link.details?.signal === 'COMM') {
                // Comm links - bidirectional adjacency
                if (sourceVisible) {
                    if (!commAdjMap.has(source)) commAdjMap.set(source, []);
                    commAdjMap.get(source)!.push(target);
                }
                if (targetVisible) {
                    if (!commAdjMap.has(target)) commAdjMap.set(target, []);
                    commAdjMap.get(target)!.push(source);
                }
            } else {
                // Hierarchical links - source is child of target
                if (targetVisible) {
                    if (!childrenMap.has(target)) childrenMap.set(target, new Set());
                    childrenMap.get(target)!.add(source);
                }
            }
        }

        // Build apiChildCount map from nodes for detecting unloaded children
        const apiChildCountMap = new Map<string, number>();
        for (const node of nodes) {
            if (node.apiChildCount != null && node.apiChildCount > 0) {
                apiChildCountMap.set(node.id, node.apiChildCount);
            }
        }

        // Compute flags for VISIBLE nodes only
        const visibleArray = Array.from(visibleNodeIds);
        for (const nodeId of visibleArray) {
            const childrenSet = childrenMap.get(nodeId);
            const children = childrenSet ? Array.from(childrenSet) : [];
            const loadedChildCount = children.length;
            const apiCount = apiChildCountMap.get(nodeId) ?? 0;
            const childCount = Math.max(loadedChildCount, apiCount);
            const hasHiddenChildren = (loadedChildCount > 0 && children.some(c => !visibleNodeIds.has(c)))
                || (apiCount > 0 && loadedChildCount === 0);

            const commNeighbors = commAdjMap.get(nodeId) ?? [];
            const hasUnrenderedCommLinks = commNeighbors.some(otherId => !visibleNodeIds.has(otherId));

            flags.set(nodeId, { childCount, hasHiddenChildren, hasUnrenderedCommLinks });
        }

        nodeFlagsRef.current = flags;
        // console.log(`[D3HybridViewer] Precompute flags: ${(performance.now() - t0).toFixed(1)}ms for ${visibleNodeIds.size} visible nodes`);
    }, [nodes, links, visibleNodeIds]);

    // Convert client coordinates to world coordinates
    const clientToWorld = useCallback((clientX: number, clientY: number): { tx: number, ty: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const t = transformRef.current;
        return {
            tx: (x - t.x) / t.k,
            ty: (y - t.y) / t.k
        };
    }, []);

    // Hit testing - find node at point
    const getNodeAtPoint = useCallback((clientX: number, clientY: number): SimNode | null => {
        const world = clientToWorld(clientX, clientY);
        if (!world) return null;

        const { tx, ty } = world;
        const d3Nodes = d3NodesRef.current;

        // Find node within radius (reverse order for top-most)
        for (let i = d3Nodes.length - 1; i >= 0; i--) {
            const node = d3Nodes[i];
            const dx = tx - node.x;
            const dy = ty - node.y;
            if (dx * dx + dy * dy < 400) { // 20px radius squared
                return node;
            }
        }
        return null;
    }, [clientToWorld]);

    // Find node whose toggle button is at point.
    // When the click is near BOTH the toggle and the node centre, pick the closer one.
    const getToggleNodeAtPoint = useCallback((clientX: number, clientY: number): SimNode | null => {
        const world = clientToWorld(clientX, clientY);
        if (!world) return null;

        const { tx, ty } = world;
        const d3Nodes = d3NodesRef.current;
        const flags = nodeFlagsRef.current;
        const toggleHitSq = 400; // 20px world radius squared

        for (let i = d3Nodes.length - 1; i >= 0; i--) {
            const node = d3Nodes[i];
            const nodeFlags = flags.get(node.id);
            if (!nodeFlags?.childCount) continue;

            // Toggle button is at (node.x + 15, node.y - 15)
            const btnX = node.x + 15;
            const btnY = node.y - 15;
            const dtx = tx - btnX;
            const dty = ty - btnY;
            const distToToggleSq = dtx * dtx + dty * dty;

            if (distToToggleSq < toggleHitSq) {
                // Also measure distance to node centre — only treat as toggle
                // if the click is closer to the toggle than to the node body
                const dnx = tx - node.x;
                const dny = ty - node.y;
                const distToNodeSq = dnx * dnx + dny * dny;
                if (distToToggleSq <= distToNodeSq) {
                    return node;
                }
            }
        }
        return null;
    }, [clientToWorld]);

    // Check if click is on toggle button (for a specific node)
    const isToggleClick = useCallback((clientX: number, clientY: number, node: SimNode): boolean => {
        const world = clientToWorld(clientX, clientY);
        if (!world) return false;

        const { tx, ty } = world;
        const toggleHitSq = 400; // 20px world radius squared

        const btnX = node.x + 15;
        const btnY = node.y - 15;
        const dtx = tx - btnX;
        const dty = ty - btnY;
        const distToToggleSq = dtx * dtx + dty * dty;

        if (distToToggleSq >= toggleHitSq) return false;

        const dnx = tx - node.x;
        const dny = ty - node.y;
        const distToNodeSq = dnx * dnx + dny * dny;
        return distToToggleSq <= distToNodeSq;
    }, [clientToWorld]);

    // Track the root node to detect site changes
    const prevRootIdRef = useRef<string | null>(null);

    // --- EFFECT 1: Topology & Simulation Setup ---
    useEffect(() => {
        if (!canvasRef.current || dimensions.width === 0) return;
        // const effectStart = performance.now();

        const width = dimensions.width;
        const height = dimensions.height;

        // Detect site change by checking if root node changed - clear position cache
        const rootNode = nodes.find(n => n.tier === 0);
        const rootId = rootNode?.id || null;
        if (rootId !== prevRootIdRef.current) {
            // console.log(`[D3HybridViewer] Root changed from ${prevRootIdRef.current} to ${rootId}, clearing position cache`);
            nodeStateRef.current.clear();
            transformRef.current = d3.zoomIdentity;
            prevRootIdRef.current = rootId;
        }

        // Setup canvas with device pixel ratio
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        // Use passed Visible Nodes
        // const t1 = performance.now();
        // Build set of nodes that have at least one edge (exclude orphans)
        const nodesWithEdges = new Set<string>();
        links.forEach(l => {
            nodesWithEdges.add(l.source);
            nodesWithEdges.add(l.target);
        });

        // Exclude pcs_skid from simulation (they're rendered as hulls only)
        // Exclude orphaned nodes (no edges) from rendering
        const visibleNodes = nodes.filter(n =>
            visibleNodeIds.has(n.id) &&
            n.type !== 'pcs_skid' &&
            nodesWithEdges.has(n.id)
        );
        // Keep all links including pcs_skid links (needed for hull calculation)
        const visibleLinks = links.filter(l =>
            visibleNodeIds.has(l.source) && visibleNodeIds.has(l.target)
        );

        // Count power edges per node (non-COMM links) for auxiliary detection
        // IMPORTANT: Use ALL links (not just visible) so nodes with hidden children
        // aren't incorrectly treated as auxiliary (e.g., combiner boxes with collapsed strings)
        const nodePowerEdgeCount = new Map<string, number>();
        links.forEach(l => {
            if (l.details?.signal !== 'COMM') {
                nodePowerEdgeCount.set(l.source, (nodePowerEdgeCount.get(l.source) || 0) + 1);
                nodePowerEdgeCount.set(l.target, (nodePowerEdgeCount.get(l.target) || 0) + 1);
            }
        });

        // Detect nodes that are "auxiliary by topology" - single power edge and not grid interconnect
        // These are monitoring taps/dead-ends that shouldn't influence main power hierarchy layout
        const isAuxiliaryByTopology = (nodeId: string, nodeType: string): boolean => {
            // Grid interconnects naturally have one edge - don't treat as auxiliary
            if (nodeType === 'grid_connection_point' || nodeType === 'grid') return false;
            const edgeCount = nodePowerEdgeCount.get(nodeId) || 0;
            return edgeCount <= 1;
        };

        // DIAGNOSTIC: If 0 links but many total links, log samples to debug
        // if (visibleLinks.length === 0 && links.length > 0) {
        //     const sampleLinks = links.slice(0, 5);
        //     const sampleNodeIds = Array.from(visibleNodeIds).slice(0, 5);
        //     console.warn(`[D3HybridViewer] ZERO LINKS BUG! Sample link sources/targets:`, sampleLinks.map(l => ({
        //         source: l.source,
        //         target: l.target,
        //         sourceType: typeof l.source,
        //         targetType: typeof l.target,
        //         sourceInVisible: visibleNodeIds.has(l.source),
        //         targetInVisible: visibleNodeIds.has(l.target),
        //     })));
        //     console.log(`[D3HybridViewer] Sample visibleNodeIds:`, sampleNodeIds);
        // }
        // console.log(`[D3HybridViewer] Filter visible: nodes=${visibleNodes.length}, links=${visibleLinks.length}, totalNodes=${nodes.length}, totalLinks=${links.length}`);

        // Separate main power hierarchy nodes from ancillary/comms nodes
        const mainNodes = visibleNodes.filter(n => !n.isAncillary);
        const ancillaryNodes = visibleNodes.filter(n => n.isAncillary);

        // Group main nodes by power tier
        const nodesByTier = new Map<number, GraphNode[]>();
        mainNodes.forEach(n => {
            if (!nodesByTier.has(n.tier)) nodesByTier.set(n.tier, []);
            nodesByTier.get(n.tier)?.push(n);
        });

        // Group ancillary nodes by comms tier
        const nodesByCommsTier = new Map<number, GraphNode[]>();
        ancillaryNodes.forEach(n => {
            const commsTier = getCommsTier(n.type);
            if (!nodesByCommsTier.has(commsTier)) nodesByCommsTier.set(commsTier, []);
            nodesByCommsTier.get(commsTier)?.push(n);
        });

        const nodeTargetCross = new Map<string, number>();
        const nodeTargetTier = new Map<string, number>();
        const crossDimension = orientation === 'TD' ? width : height;
        const tierNumbers = Array.from(nodesByTier.keys()).sort((a, b) => a - b);
        const commsTierNumbers = Array.from(nodesByCommsTier.keys()).sort((a, b) => a - b);

        // Canvas layout: Main hierarchy gets left portion, Comms gets right portion
        const mainSectionWidth = crossDimension * 0.60;
        const commsSectionStart = crossDimension * 0.62;
        const commsSectionWidth = crossDimension * 0.36;

        const getNumericSuffix = (id: string): number => {
            const match = id.match(/(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
        };

        // Build parent-child relationships for positioning (only visible nodes)
        // Use node parentId (matches corrected hierarchy) instead of link direction
        const childToParent = new Map<string, string>();
        const parentToChildrenSet = new Map<string, Set<string>>();
        const visibleNodeSet = new Set(visibleNodes.map(n => n.id));

        visibleNodes.forEach(n => {
            if (n.parentId && visibleNodeSet.has(n.parentId)) {
                childToParent.set(n.id, n.parentId);
                if (!parentToChildrenSet.has(n.parentId)) parentToChildrenSet.set(n.parentId, new Set());
                parentToChildrenSet.get(n.parentId)!.add(n.id);
            }
        });

        // Convert Sets to arrays for easier iteration
        const parentToChildren = new Map<string, string[]>();
        parentToChildrenSet.forEach((children, parent) => {
            parentToChildren.set(parent, Array.from(children));
        });

        // Calculate subtree width for each node (how much horizontal space it needs)
        // This is done bottom-up so we know child widths before calculating parent widths
        const nodeSubtreeWidth = new Map<string, number>();
        const minNodeSpacing = 80; // Minimum spacing between sibling nodes (increased from 55)

        // Process tiers bottom-up to calculate subtree widths
        const reverseTiers = [...tierNumbers].reverse();
        reverseTiers.forEach(tier => {
            const tierNodes = nodesByTier.get(tier) || [];
            tierNodes.forEach(node => {
                const children = parentToChildren.get(node.id) || [];
                // Filter out children that are:
                // 1. Not visible
                // 2. Marked as ancillary (isAncillary flag)
                // 3. Auxiliary by topology (single power edge, not grid interconnect)
                const visibleChildren = children.filter(c => {
                    if (!visibleNodeSet.has(c)) return false;
                    const childNode = visibleNodes.find(n => n.id === c);
                    if (!childNode) return false;
                    // Exclude ancillary nodes from main hierarchy width calculation
                    if (childNode.isAncillary) return false;
                    // Exclude single-power-edge nodes (monitoring taps)
                    if (isAuxiliaryByTopology(c, childNode.type)) return false;
                    return true;
                });

                if (visibleChildren.length === 0) {
                    // Leaf node - just needs space for itself
                    nodeSubtreeWidth.set(node.id, minNodeSpacing);
                } else {
                    // Sum of children's subtree widths
                    // This ensures parent expands enough to fit all visible children
                    const childWidths = visibleChildren.map(childId =>
                        nodeSubtreeWidth.get(childId) || minNodeSpacing
                    );
                    const totalChildWidth = childWidths.reduce((sum, w) => sum + w, 0);

                    // Ensure parent has at least min spacing, but otherwise take full child width
                    // No compression - let it expand as needed
                    nodeSubtreeWidth.set(node.id, Math.max(minNodeSpacing, totalChildWidth));
                }
            });
        });

        // Build set of nodes on the POI path (all descendants of grid_connection_point)
        // These nodes should be prioritized for centering
        const nodesOnPOIPath = new Set<string>();
        const gridNode = visibleNodes.find(n => n.type === 'grid_connection_point' || n.type === 'grid');
        if (gridNode) {
            // BFS from grid node to find all descendants
            const queue = [gridNode.id];
            nodesOnPOIPath.add(gridNode.id);
            while (queue.length > 0) {
                const currentId = queue.shift()!;
                const children = parentToChildren.get(currentId) || [];
                children.forEach(childId => {
                    if (!nodesOnPOIPath.has(childId)) {
                        nodesOnPOIPath.add(childId);
                        queue.push(childId);
                    }
                });
            }
        }

        // Position nodes top-down, allocating space based on subtree widths
        const assignMainPositions = (nodeIds: string[], tier: number) => {
            if (nodeIds.length === 0) return;

            // Sort nodes by numeric suffix for consistent ordering
            const sortedIds = [...nodeIds].sort((a, b) => {
                const numA = a.match(/(\d+)$/)?.[1] || '0';
                const numB = b.match(/(\d+)$/)?.[1] || '0';
                return parseInt(numA, 10) - parseInt(numB, 10);
            });

            // Group nodes by their parent
            const nodesByParent = new Map<string, string[]>();
            const orphans: string[] = [];

            sortedIds.forEach(id => {
                const parent = childToParent.get(id);
                if (parent && nodeTargetCross.has(parent)) {
                    if (!nodesByParent.has(parent)) nodesByParent.set(parent, []);
                    nodesByParent.get(parent)!.push(id);
                } else {
                    orphans.push(id);
                }
            });

            // For tier 0 (roots) or all orphans - position based on subtree widths
            if (tier === 0 || orphans.length === sortedIds.length) {
                const nodesToPosition = tier === 0 ? sortedIds : orphans;

                // Find POI node (grid_connection_point or grid) - should be centered
                const poiIndex = nodesToPosition.findIndex(id => {
                    const node = visibleNodes.find(n => n.id === id);
                    return node && (node.type === 'grid_connection_point' || node.type === 'grid');
                });

                if (poiIndex >= 0 && nodesToPosition.length > 1) {
                    // POI found - center it and position others around it
                    const poiId = nodesToPosition[poiIndex];
                    const poiWidth = nodeSubtreeWidth.get(poiId) || minNodeSpacing;
                    const centerX = mainSectionWidth / 2;

                    // Center the POI
                    nodeTargetCross.set(poiId, centerX);

                    // Position nodes before POI to the left
                    const leftNodes = nodesToPosition.slice(0, poiIndex);
                    let leftX = centerX - poiWidth / 2;
                    for (let i = leftNodes.length - 1; i >= 0; i--) {
                        const id = leftNodes[i];
                        const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                        leftX -= width;
                        nodeTargetCross.set(id, leftX + width / 2);
                    }

                    // Position nodes after POI to the right
                    const rightNodes = nodesToPosition.slice(poiIndex + 1);
                    let rightX = centerX + poiWidth / 2;
                    rightNodes.forEach(id => {
                        const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                        nodeTargetCross.set(id, rightX + width / 2);
                        rightX += width;
                    });
                } else {
                    // No POI or single node - center all nodes together
                    const totalWidth = nodesToPosition.reduce((sum, id) =>
                        sum + (nodeSubtreeWidth.get(id) || minNodeSpacing), 0);

                    let currentX = (mainSectionWidth - totalWidth) / 2;

                    nodesToPosition.forEach(id => {
                        const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                        nodeTargetCross.set(id, currentX + width / 2);
                        currentX += width;
                    });
                }
                return;
            }

            // Position children under each parent based on their subtree widths
            // Prioritize children on the POI path (grid_connection_point descendants) to stay centered
            nodesByParent.forEach((children, parentId) => {
                const parentPos = nodeTargetCross.get(parentId) || mainSectionWidth / 2;

                if (children.length === 1) {
                    // Single child - center directly under parent
                    nodeTargetCross.set(children[0], parentPos);
                    return;
                }

                // Find the primary child - prioritize POI path, then fall back to largest subtree
                // This child should be centered under the parent
                let primaryChildId: string | null = null;

                // First priority: child on the POI path (descendant of grid_connection_point)
                const poiPathChildren = children.filter(id => nodesOnPOIPath.has(id));
                if (poiPathChildren.length === 1) {
                    primaryChildId = poiPathChildren[0];
                } else if (poiPathChildren.length > 1) {
                    // Multiple children on POI path - pick the one with largest subtree
                    let maxWidth = 0;
                    poiPathChildren.forEach(id => {
                        const width = nodeSubtreeWidth.get(id) || 0;
                        if (width > maxWidth) {
                            maxWidth = width;
                            primaryChildId = id;
                        }
                    });
                }

                // Fallback: if no child on POI path, use largest subtree
                if (!primaryChildId) {
                    let maxSubtreeWidth = 0;
                    children.forEach(id => {
                        const width = nodeSubtreeWidth.get(id) || 0;
                        if (width > maxSubtreeWidth) {
                            maxSubtreeWidth = width;
                            primaryChildId = id;
                        }
                    });
                }

                // Safety fallback
                if (!primaryChildId) primaryChildId = children[0];

                // Separate primary child from auxiliary children
                const auxChildren = children.filter(id => id !== primaryChildId);

                // Sort auxiliary children by numeric suffix
                auxChildren.sort((a, b) => {
                    const numA = a.match(/(\d+)$/)?.[1] || '0';
                    const numB = b.match(/(\d+)$/)?.[1] || '0';
                    return parseInt(numA, 10) - parseInt(numB, 10);
                });

                // Center the primary child directly under the parent
                nodeTargetCross.set(primaryChildId, parentPos);

                // Position auxiliary children to the sides
                // Split them evenly left and right of the primary child
                const primaryWidth = nodeSubtreeWidth.get(primaryChildId) || minNodeSpacing;
                const leftAux = auxChildren.slice(0, Math.floor(auxChildren.length / 2));
                const rightAux = auxChildren.slice(Math.floor(auxChildren.length / 2));

                // Position left auxiliary children
                let leftX = parentPos - primaryWidth / 2;
                for (let i = leftAux.length - 1; i >= 0; i--) {
                    const id = leftAux[i];
                    const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                    leftX -= width;
                    nodeTargetCross.set(id, leftX + width / 2);
                }

                // Position right auxiliary children
                let rightX = parentPos + primaryWidth / 2;
                rightAux.forEach(id => {
                    const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                    nodeTargetCross.set(id, rightX + width / 2);
                    rightX += width;
                });
            });

            // Position orphans in remaining space
            if (orphans.length > 0) {
                const totalOrphanWidth = orphans.reduce((sum, id) =>
                    sum + (nodeSubtreeWidth.get(id) || minNodeSpacing), 0);
                let currentX = mainSectionWidth * 0.05;

                orphans.forEach(id => {
                    const width = nodeSubtreeWidth.get(id) || minNodeSpacing;
                    nodeTargetCross.set(id, currentX + width / 2);
                    currentX += width;
                });
            }
        };

        // Position main power hierarchy nodes (process in tier order so parents are positioned first)
        tierNumbers.forEach(tier => {
            const tierNodes = nodesByTier.get(tier) || [];
            tierNodes.sort((a, b) => {
                const numA = getNumericSuffix(a.id);
                const numB = getNumericSuffix(b.id);
                if (numA !== numB) return numA - numB;
                return a.id.localeCompare(b.id, undefined, { numeric: true });
            });
            assignMainPositions(tierNodes.map(n => n.id), tier);
        });

        // --- Hierarchical layout for ancillary/comms nodes ---
        // Build parent-child from COMM and "contains" links between ancillary nodes.
        // Use comms tier (type-based) to determine direction for COMM links;
        // "contains" links use source=container, target=contained.
        const ancillarySet = new Set(ancillaryNodes.map(n => n.id));
        const commsChildToParent = new Map<string, string>();
        const commsParentToChildren = new Map<string, string[]>();

        const addCommsEdge = (parentId: string, childId: string) => {
            if (parentId === childId) return;
            if (commsChildToParent.has(childId)) return; // already assigned
            commsChildToParent.set(childId, parentId);
            if (!commsParentToChildren.has(parentId)) commsParentToChildren.set(parentId, []);
            commsParentToChildren.get(parentId)!.push(childId);
        };

        visibleLinks.forEach(l => {
            const sId = l.source;
            const tId = l.target;
            if (!ancillarySet.has(sId) || !ancillarySet.has(tId)) return;

            const signal = l.details?.signal;
            const relation = l.details?.relation;

            if (relation === 'contains') {
                // "contains": source is container (parent), target is contained (child)
                addCommsEdge(sId, tId);
            } else if (signal === 'COMM') {
                const sNode = ancillaryNodes.find(n => n.id === sId);
                const tNode = ancillaryNodes.find(n => n.id === tId);
                if (!sNode || !tNode) return;
                const sTier = getCommsTier(sNode.type);
                const tTier = getCommsTier(tNode.type);
                // Lower commsTier = parent; equal tier: source = parent
                if (sTier <= tTier) {
                    addCommsEdge(sId, tId);
                } else {
                    addCommsEdge(tId, sId);
                }
            }
        });

        // Calculate actual hierarchy depth via BFS from comms roots
        // (replaces type-based getCommsTier for vertical positioning)
        const commsDepth = new Map<string, number>();
        const commsRoots = ancillaryNodes
            .filter(n => !commsChildToParent.has(n.id))
            .sort((a, b) => {
                const numA = getNumericSuffix(a.id);
                const numB = getNumericSuffix(b.id);
                if (numA !== numB) return numA - numB;
                return a.id.localeCompare(b.id, undefined, { numeric: true });
            });

        // BFS to assign depths
        const depthQueue = [...commsRoots.map(n => n.id)];
        commsRoots.forEach(n => commsDepth.set(n.id, 1));
        let depthQueueIdx = 0;
        while (depthQueueIdx < depthQueue.length) {
            const currentId = depthQueue[depthQueueIdx++];
            const currentDepth = commsDepth.get(currentId) || 1;
            const children = commsParentToChildren.get(currentId) || [];
            children.forEach(childId => {
                if (!commsDepth.has(childId)) {
                    commsDepth.set(childId, currentDepth + 1);
                    depthQueue.push(childId);
                }
            });
        }
        // Assign depth to any orphaned ancillary node not reached by BFS
        ancillaryNodes.forEach(n => {
            if (!commsDepth.has(n.id)) commsDepth.set(n.id, getCommsTier(n.type));
        });

        // Calculate subtree widths for comms hierarchy (bottom-up by depth)
        const commsMinSpacing = 75;
        const commsSubtreeWidth = new Map<string, number>();
        const maxCommsDepth = Math.max(...Array.from(commsDepth.values()), 1);

        for (let d = maxCommsDepth; d >= 1; d--) {
            ancillaryNodes.forEach(node => {
                if (commsDepth.get(node.id) !== d) return;
                const children = (commsParentToChildren.get(node.id) || [])
                    .filter(c => ancillarySet.has(c));
                if (children.length === 0) {
                    commsSubtreeWidth.set(node.id, commsMinSpacing);
                } else {
                    const total = children.reduce((sum, cId) =>
                        sum + (commsSubtreeWidth.get(cId) || commsMinSpacing), 0);
                    commsSubtreeWidth.set(node.id, Math.max(commsMinSpacing, total));
                }
            });
        }

        // Position roots spread across comms section
        const totalRootWidth = commsRoots.reduce((sum, n) =>
            sum + (commsSubtreeWidth.get(n.id) || commsMinSpacing), 0);
        let commsCurrentX = commsSectionStart + (commsSectionWidth - Math.min(totalRootWidth, commsSectionWidth)) / 2;

        commsRoots.forEach(root => {
            const rootWidth = commsSubtreeWidth.get(root.id) || commsMinSpacing;
            nodeTargetCross.set(root.id, commsCurrentX + rootWidth / 2);
            commsCurrentX += rootWidth;
        });

        // BFS to position children under their parents
        const commsQueue = [...commsRoots.map(n => n.id)];
        let commsQueueIdx = 0;
        while (commsQueueIdx < commsQueue.length) {
            const currentId = commsQueue[commsQueueIdx++];
            const children = (commsParentToChildren.get(currentId) || [])
                .filter(c => ancillarySet.has(c))
                .sort((a, b) => {
                    const numA = getNumericSuffix(a);
                    const numB = getNumericSuffix(b);
                    if (numA !== numB) return numA - numB;
                    return a.localeCompare(b, undefined, { numeric: true });
                });

            if (children.length === 0) continue;

            const parentPos = nodeTargetCross.get(currentId) || (commsSectionStart + commsSectionWidth / 2);
            const childrenTotalWidth = children.reduce((sum, cId) =>
                sum + (commsSubtreeWidth.get(cId) || commsMinSpacing), 0);

            let childX = parentPos - childrenTotalWidth / 2;
            children.forEach(childId => {
                const childWidth = commsSubtreeWidth.get(childId) || commsMinSpacing;
                nodeTargetCross.set(childId, childX + childWidth / 2);
                childX += childWidth;
                commsQueue.push(childId);
            });
        }

        // Set comms tier from actual hierarchy depth (not type-based)
        ancillaryNodes.forEach(n => {
            nodeTargetTier.set(n.id, commsDepth.get(n.id) || getCommsTier(n.type));
            if (!nodeTargetCross.has(n.id)) {
                nodeTargetCross.set(n.id, commsSectionStart + commsSectionWidth / 2);
            }
        });

        // Dead-end leaf nodes (e.g. revenue meters) should display at their parent's
        // tier level, positioned close beside the parent rather than one tier below.
        // They keep their data tier (parent+1) for correct subtree positioning,
        // but override both visual tier and cross-axis position here.
        // Exclude strings/pv_modules — they are true hierarchy leaves that belong
        // one tier below their parent even though they have only 1 power edge.
        const deadEndExcludedTypes = new Set(['string', 'pv_module', 'racking_system', 'tracker_controller']);
        const deadEndOffset = 50; // pixels offset from parent in cross-axis
        visibleNodes.forEach(n => {
            if (!n.isAncillary && !deadEndExcludedTypes.has(n.type) && isAuxiliaryByTopology(n.id, n.type)) {
                const parentId = childToParent.get(n.id);
                if (parentId) {
                    const parentNode = visibleNodes.find(p => p.id === parentId);
                    if (parentNode && !parentNode.isAncillary) {
                        nodeTargetTier.set(n.id, parentNode.tier);
                        // Place right beside parent in cross-axis
                        const parentCross = nodeTargetCross.get(parentId);
                        if (parentCross !== undefined) {
                            // Count how many dead-end siblings share this parent
                            // to offset each one slightly
                            const existingSiblings = visibleNodes.filter(s =>
                                s.id !== n.id &&
                                !s.isAncillary &&
                                !deadEndExcludedTypes.has(s.type) &&
                                isAuxiliaryByTopology(s.id, s.type) &&
                                childToParent.get(s.id) === parentId
                            );
                            const sibIdx = existingSiblings.filter(s => s.id < n.id).length;
                            nodeTargetCross.set(n.id, parentCross + deadEndOffset + sibIdx * deadEndOffset);
                        }
                    }
                }
            }
        });

        // Uniform tier spacing for power hierarchy
        const getTierPosition = (tier: number, isAncillary = false): number => {
            const baseSpacing = 120;

            if (isAncillary) {
                // Ancillary nodes appear ABOVE the grid (negative Y in TD mode)
                // Higher comms tiers go further up (more negative)
                const commsBaseSpacing = 70;
                // Offset by -50 so tier 1 is above grid, and higher tiers go further up
                return -50 - (tier * commsBaseSpacing);
            }

            return tier * baseSpacing;
        };

        // Initialize D3 Nodes
        // Ancillary nodes get fixed positions (fx/fy) pinned to their CALCULATED
        // target positions so they can't drift or exert forces on the main hierarchy.
        const d3Nodes: SimNode[] = visibleNodes.map(n => {
            const prev = nodeStateRef.current.get(n.id);
            const targetCross = nodeTargetCross.get(n.id) || (orientation === 'TD' ? width / 2 : height / 2);
            const effectiveTier = nodeTargetTier.get(n.id) ?? n.tier;
            const tierPos = getTierPosition(effectiveTier, n.isAncillary);
            // Ancillary nodes always use calculated positions (not cached) to
            // prevent pinning to stale positions after layout changes.
            const calcX = orientation === 'TD' ? targetCross : tierPos;
            const calcY = orientation === 'TD' ? tierPos : targetCross;
            const xPos = n.isAncillary ? calcX : (prev ? prev.x : calcX);
            const yPos = n.isAncillary ? calcY : (prev ? prev.y : calcY);
            return {
                ...n,
                x: xPos,
                y: yPos,
                vx: prev && !n.isAncillary ? prev.vx : 0,
                vy: prev && !n.isAncillary ? prev.vy : 0,
                fx: n.isAncillary ? calcX : null,
                fy: n.isAncillary ? calcY : null,
                targetCross: targetCross,
                effectiveTier: effectiveTier,
                anchorX: n.isAncillary ? calcX : undefined,
                anchorY: n.isAncillary ? calcY : undefined
            };
        });

        const nodeMap = new Map(d3Nodes.map(n => [n.id, n]));

        // Initialize Links - d3Links for rendering, simLinks for physics
        const d3Links: SimLink[] = visibleLinks.map(l => ({ ...l }));
        // Exclude from physics: COMM links AND links where either endpoint is ancillary.
        // Ancillary nodes (gateways, sensors, etc.) can have AC/DC power feeds (e.g. PNL-AUX → DAS-TS1),
        // and those link forces would pull main hierarchy nodes toward the comms section,
        // bending the main power flow layout.
        const simLinks = d3Links.filter(l => {
            const signal = l.details?.signal;
            // Exclude COMM edges entirely from physics simulation
            if (signal === 'COMM') return false;
            // Ensure both endpoints exist
            const sourceNode = nodeMap.get(l.source as string);
            const targetNode = nodeMap.get(l.target as string);
            if (!sourceNode || !targetNode) return false;
            // Exclude links where either endpoint is ancillary
            if (sourceNode.isAncillary || targetNode.isAncillary) return false;
            return true;
        });

        // Store refs
        d3NodesRef.current = d3Nodes;
        d3LinksRef.current = d3Links;
        nodeMapRef.current = nodeMap;

        // Stop existing simulation
        if (simulationRef.current) simulationRef.current.stop();

        // Detect incremental update: node count changed but not a full site reload.
        // Full reload = prevCount was 0 or root node changed.
        const prevCount = prevNodeCountRef.current;
        const isIncremental = prevCount > 0 && d3Nodes.length !== prevCount;
        prevNodeCountRef.current = d3Nodes.length;

        // For incremental updates, zero out velocity on existing nodes to prevent drift
        if (isIncremental) {
            d3Nodes.forEach(n => {
                const prev = nodeStateRef.current.get(n.id);
                if (prev) {
                    n.vx = 0;
                    n.vy = 0;
                }
            });
        }

        // Create simulation — strong positional forces ensure nodes always
        // return to sorted/aligned positions after expand/contract operations
        const simulation = d3.forceSimulation(d3Nodes)
            .force("link", d3.forceLink(simLinks)
                .id((d: any) => d.id)
                .distance(60)
                .strength(0.5)
            )
            .force("charge", d3.forceManyBody()
                .strength((d: any) => d.isAncillary ? 0 : -80)
                .distanceMax(150))
            .force("collide", d3.forceCollide()
                .radius((d: any) => d.isAncillary ? 0 : 30)
                .strength(0.7))
            .velocityDecay(0.65)  // Lower = more momentum to reach targets
            .alpha(isIncremental ? 0.3 : 1.0)            // Moderate alpha for incremental, full for initial
            .alphaDecay(isIncremental ? 0.08 : 0.05);    // Settle in ~30 ticks for incremental

        // Strong cross-axis forces ensure nodes snap to their calculated positions.
        // This prevents overlap/drift after expand/contract operations.
        const crossStrength = (d: any) => {
            // Grid interconnect points should anchor the layout
            if (d.type === 'grid_connection_point' || d.type === 'grid') return 1.0;
            // Main power hierarchy nodes have strong pull to target position
            if (!d.isAncillary) return 0.9;
            // Ancillary nodes have lower priority - they shouldn't pull main nodes
            return 0.6;
        };

        if (orientation === 'TD') {
            simulation
                .force("y", d3.forceY((d: any) => getTierPosition(d.effectiveTier, d.isAncillary)).strength(4.0))
                .force("x", d3.forceX((d: any) => d.targetCross).strength(crossStrength));
        } else {
            simulation
                .force("x", d3.forceX((d: any) => getTierPosition(d.effectiveTier, d.isAncillary)).strength(4.0))
                .force("y", d3.forceY((d: any) => d.targetCross).strength(crossStrength));
        }

        simulationRef.current = simulation;

        // Initial transform
        if (!transformRef.current || transformRef.current === d3.zoomIdentity) {
            const tx = width / 2;
            const ty = orientation === 'TD' ? 50 : height / 2;
            const offsetX = orientation === 'TD' ? 0 : -100;
            transformRef.current = d3.zoomIdentity.translate(tx + offsetX, ty).scale(0.6);
        }

        // Simulation tick handler - just flag for redraw, don't schedule directly
        simulation.on("tick", () => {
            // Store node positions
            d3Nodes.forEach(n => nodeStateRef.current.set(n.id, { x: n.x, y: n.y, vx: n.vx, vy: n.vy }));
            needsRedrawRef.current = true;

            // Stop simulation when settled to save CPU
            if (simulation.alpha() < 0.03) {
                simulation.stop();
            }
        });

        // Throttled render loop - ~30fps during simulation, responsive to user input
        let running = true;
        let lastRenderTime = 0;
        const MIN_FRAME_TIME = 33; // ~30fps max during simulation

        const renderLoop = () => {
            if (!running) return;

            const now = performance.now();
            const elapsed = now - lastRenderTime;

            // Throttle renders during simulation, but always render if user-triggered (zoom/selection)
            if (needsRedrawRef.current && drawFnRef.current) {
                if (elapsed >= MIN_FRAME_TIME || !simulationRef.current || simulationRef.current.alpha() < 0.02) {
                    drawFnRef.current();
                    needsRedrawRef.current = false;
                    lastRenderTime = now;
                }
            }

            animationFrameRef.current = requestAnimationFrame(renderLoop);
        };
        renderLoop();

        // Initial draw
        needsRedrawRef.current = true;

        // console.log(`[D3HybridViewer] Simulation setup total: ${(performance.now() - effectStart).toFixed(1)}ms`);

        return () => {
            running = false;
            if (simulationRef.current) simulationRef.current.stop();
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [nodes, links, dimensions, expandedIds, orientation, visibleNodeIds]);

    // --- EFFECT 2: Visual Updates (Selection, Power Mode) ---
    useEffect(() => {
        // Trigger redraw when visual state changes
        needsRedrawRef.current = true;
    }, [selectedNodeIds, powerVisMode, highlightedPath]);

    // --- EFFECT 2b: Loading spinner animation for expanding node ---
    const expandingNodeIdRef = useRef<string | null>(null);
    const loadingAngleRef = useRef(0);
    useEffect(() => {
        expandingNodeIdRef.current = expandingNodeId ?? null;
        if (!expandingNodeId) return;

        // Animate spinner at ~30fps while node is loading
        let running = true;
        const animate = () => {
            if (!running) return;
            loadingAngleRef.current += 0.15;
            needsRedrawRef.current = true;
            requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        return () => { running = false; };
    }, [expandingNodeId]);

    // --- Zoom Setup (set up once, stable across simulation changes) ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Only create zoom behavior once
        if (zoomBehaviorRef.current) return;

        const canvasSelection = d3.select(canvas);

        // Create zoom behavior - filter to allow zoom only on background
        const zoom = d3.zoom<HTMLCanvasElement, unknown>()
            .scaleExtent([0.1, 5])
            .filter((event: any) => {
                // Always allow wheel zoom
                if (event.type === 'wheel') return true;
                // For mousedown, check if over a node (only on click start, not every move)
                if (event.type === 'mousedown') {
                    const rect = canvas.getBoundingClientRect();
                    const x = event.clientX - rect.left;
                    const y = event.clientY - rect.top;
                    const t = transformRef.current;
                    const tx = (x - t.x) / t.k;
                    const ty = (y - t.y) / t.k;

                    const nodes = d3NodesRef.current;
                    for (let i = nodes.length - 1; i >= 0; i--) {
                        const node = nodes[i];
                        const dx = tx - node.x;
                        const dy = ty - node.y;
                        if (dx * dx + dy * dy < 400) {
                            return false; // On a node, don't start pan
                        }
                    }
                }
                return true;
            })
            .on("zoom", (event: d3.D3ZoomEvent<HTMLCanvasElement, unknown>) => {
                transformRef.current = event.transform;
                needsRedrawRef.current = true;
            });

        zoomBehaviorRef.current = zoom;
        canvasSelection.call(zoom);

        // Apply initial transform if set
        if (transformRef.current && transformRef.current !== d3.zoomIdentity) {
            canvasSelection.call(zoom.transform, transformRef.current);
        }

        return () => {
            canvasSelection.on('.zoom', null);
            zoomBehaviorRef.current = null;
        };
    }, []); // Empty deps - set up once

    // --- Interaction Handlers (separate from zoom) ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasSelection = d3.select(canvas);

        // Track click vs drag
        let clickStartTime = 0;
        let clickStartPos = { x: 0, y: 0 };
        let clickedOnBackground = false;
        const CLICK_THRESHOLD = 5;
        const CLICK_TIME_THRESHOLD = 300;

        // Mouse down handler
        canvasSelection.on("mousedown.interaction", (event: MouseEvent) => {
            clickStartTime = Date.now();
            clickStartPos = { x: event.clientX, y: event.clientY };
            clickedOnBackground = false;

            // First check if clicking on a toggle button (can be outside node body radius)
            const toggleNode = getToggleNodeAtPoint(event.clientX, event.clientY);
            if (toggleNode) {
                onToggleExpand(toggleNode.id);
                return;
            }

            // Then check for node body click
            const node = getNodeAtPoint(event.clientX, event.clientY);
            if (node) {
                // Start drag - zoom filter already prevents zoom on nodes
                dragNodeRef.current = node;
                isDraggingRef.current = false;
                node.fx = node.x;
                node.fy = node.y;
                setCursorStyle('grabbing');
                simulationRef.current?.alphaTarget(0.3).restart();
            } else {
                // Clicked on background (not on a node)
                clickedOnBackground = true;
            }
        });

        // Mouse move handler - update cursor and handle drag
        canvasSelection.on("mousemove.interaction", (event: MouseEvent) => {
            // Update cursor based on what's under the mouse
            if (dragNodeRef.current) {
                // Currently dragging
                setCursorStyle('grabbing');

                const dx = event.clientX - clickStartPos.x;
                const dy = event.clientY - clickStartPos.y;
                if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) {
                    isDraggingRef.current = true;
                }

                const rect = canvas.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const y = event.clientY - rect.top;

                const t = transformRef.current;
                dragNodeRef.current.fx = (x - t.x) / t.k;
                dragNodeRef.current.fy = (y - t.y) / t.k;
            } else {
                // Not dragging - update cursor and tooltip based on hover
                const toggleNode = getToggleNodeAtPoint(event.clientX, event.clientY);
                if (toggleNode) {
                    setCursorStyle('pointer');
                    setTooltip(null);
                } else {
                    const node = getNodeAtPoint(event.clientX, event.clientY);
                    if (node) {
                        setCursorStyle('grab');
                        // Determine tooltip text based on click phase
                        const isSelected = selectedNodeIds.has(node.id);
                        let tipText: string;
                        if (!isSelected) {
                            tipText = 'Click to select';
                        } else if (nodeClickPhase === 1) {
                            tipText = 'Click to expand connections';
                        } else if (nodeClickPhase === 2) {
                            tipText = 'Click to expand full chain';
                        } else {
                            tipText = 'Click to collapse';
                        }
                        const rect = canvas.getBoundingClientRect();
                        setTooltip({
                            x: event.clientX - rect.left,
                            y: event.clientY - rect.top - 30,
                            text: tipText
                        });
                    } else {
                        setCursorStyle('default');
                        setTooltip(null);
                    }
                }
            }
        });

        // Mouse up handler
        canvasSelection.on("mouseup.interaction", (event: MouseEvent) => {
            const clickDuration = Date.now() - clickStartTime;
            const dx = event.clientX - clickStartPos.x;
            const dy = event.clientY - clickStartPos.y;
            const isClick = clickDuration < CLICK_TIME_THRESHOLD &&
                Math.abs(dx) < CLICK_THRESHOLD &&
                Math.abs(dy) < CLICK_THRESHOLD;

            setTooltip(null); // Clear tooltip on any click/release
            if (dragNodeRef.current) {
                if (isClick && !isDraggingRef.current) {
                    if (onNodeClick) onNodeClick(dragNodeRef.current);
                }

                // Ancillary nodes snap back to their fixed calculated position;
                // main hierarchy nodes are freed to settle via simulation forces.
                if (dragNodeRef.current.isAncillary && dragNodeRef.current.anchorX != null) {
                    dragNodeRef.current.fx = dragNodeRef.current.anchorX;
                    dragNodeRef.current.fy = dragNodeRef.current.anchorY!;
                } else {
                    dragNodeRef.current.fx = null;
                    dragNodeRef.current.fy = null;
                }
                dragNodeRef.current = null;
                simulationRef.current?.alphaTarget(0);
                setCursorStyle('grab'); // Back to grab after releasing
            } else if (isClick && clickedOnBackground) {
                // Background click - deselect
                if (onBackgroundClick) onBackgroundClick();
            }
            isDraggingRef.current = false;
            clickedOnBackground = false;
        });

        // Mouse leave handler
        canvasSelection.on("mouseleave.interaction", () => {
            if (dragNodeRef.current) {
                if (dragNodeRef.current.isAncillary && dragNodeRef.current.anchorX != null) {
                    dragNodeRef.current.fx = dragNodeRef.current.anchorX;
                    dragNodeRef.current.fy = dragNodeRef.current.anchorY!;
                } else {
                    dragNodeRef.current.fx = null;
                    dragNodeRef.current.fy = null;
                }
                dragNodeRef.current = null;
                simulationRef.current?.alphaTarget(0);
            }
            isDraggingRef.current = false;
            clickedOnBackground = false;
            setCursorStyle('default');
            setTooltip(null);
        });

        return () => {
            canvasSelection.on('.interaction', null);
        };
    }, [getNodeAtPoint, getToggleNodeAtPoint, isToggleClick, onToggleExpand, onNodeClick, onBackgroundClick, selectedNodeIds, nodeClickPhase]);

    return (
        <div
            ref={containerRef}
            className="w-full h-full bg-[var(--color-bg-primary)] overflow-hidden relative"
        >
            <canvas
                ref={canvasRef}
                className="w-full h-full block"
                style={{ cursor: cursorStyle }}
            />
            {/* Recentre button - top left */}
            <button
                onClick={() => centerOnNodes()}
                className="absolute top-4 left-4 bg-[var(--color-surface)] hover:bg-emerald-600 text-[var(--color-text-primary)] hover:text-white px-3 py-2 rounded-lg text-sm font-medium z-10 transition-colors flex items-center gap-2 border border-[var(--color-border)] hover:border-emerald-500"
                title="Center view on all visible nodes"
            >
                <Focus size={16} />
                <span>Recentre</span>
            </button>
            <div className="absolute bottom-4 right-4 bg-[var(--color-surface)] p-2 rounded text-xs eg-text-secondary pointer-events-none">
                {nodes.length} nodes • {links.length} links (Canvas)
            </div>
            {tooltip && (
                <div
                    className="absolute pointer-events-none bg-[var(--color-surface)] eg-text-primary text-xs px-2 py-1 rounded shadow-lg border border-[var(--color-border)] whitespace-nowrap"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translateX(-50%)',
                    }}
                >
                    {tooltip.text}
                </div>
            )}
        </div>
    );
};

export default D3HybridViewer;
